import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { User, Mail, Phone, Divide } from "lucide-react";
import { FaLinkedin } from "react-icons/fa";
import { getFirstEmail, getAllEmails } from "../../utils/emailHelpers";

export const RecruitmentItem = ({ candidate, selected, onSelect, onNavigate }) => {
  if (!candidate || !candidate.individual) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mb-2">
        <p className="text-sm text-gray-500">Candidat non disponible</p>
      </div>
    );
  }

  const individual = candidate.individual;

  const handleClick = (e) => {
    // Ne pas naviguer si on clique sur la checkbox ou un lien LinkedIn
    if (e.target.type === 'checkbox' || e.target.closest('a')) {
      return;
    }
    if (onNavigate) {
      onNavigate(individual._id);
    }
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(candidate._id);
    }
  };

  return (
    <div 
      className={`bg-white border rounded-lg p-3 mb-2 shadow-sm transition-all duration-200 cursor-grab active:cursor-grabbing ${
        selected 
          ? 'border-blue-300 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:shadow-md'
      }`}
      onClick={handleClick}
    >
      {/* Header avec checkbox, photo et nom */}
      <div className="flex items-start space-x-2 mb-2">
        <input
          type="checkbox"
          checked={selected || false}
          onChange={handleCheckboxClick}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
        />
        {individual.photo ? (
          <img
            src={individual.photo}
            alt={individual.Name}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <User size={16} className="text-gray-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Nom et LinkedIn */}
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium text-gray-900 truncate hover:text-blue-600 m-0">
              {individual.Name || "Nom non disponible"}
            </h4>
            {individual.linkedinUrl && (
              <a
                href={individual.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                title="Voir le profil LinkedIn"
                onClick={(e) => e.stopPropagation()}
              >
                <FaLinkedin size={14} />
              </a>
            )}
          </div>

          {/* Email professionnel */}
          {getAllEmails(individual.professionalEmail).length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
              <Mail size={11} className="text-blue-500 flex-shrink-0" />
              <span className="truncate">{getFirstEmail(individual.professionalEmail)}</span>
            </div>
          )}

          {/* Email personnel */}
          {getAllEmails(individual.personalEmail).length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
              <Mail size={11} className="text-green-500 flex-shrink-0" />
              <span className="truncate">{getFirstEmail(individual.personalEmail)}</span>
            </div>
          )}

          {/* Téléphone */}
          {individual.phoneNumber && (
            <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
              <Phone size={11} className="text-gray-500 flex-shrink-0" />
              <span className="truncate">{individual.phoneNumber}</span>
            </div>
          )}
        </div>
      </div>

      {/* Date de déplacement à cette étape */}
      <div className="mt-2">
        <p className="text-xs text-gray-400">
          {candidate.stageHistory && candidate.stageHistory.length > 0
            ? `Déplacé le ${new Date(candidate.stageHistory[candidate.stageHistory.length - 1].date).toLocaleDateString('fr-FR')}`
            : `Ajouté le ${new Date(candidate.addedAt).toLocaleDateString('fr-FR')}`
          }
        </p>
      </div>
    </div>
  );
};

export default function SortableRecruitmentItem({ id, candidate, containerId, selected, onSelect, onNavigate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: {
      type: 'candidate',
      candidate,
      containerId,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <RecruitmentItem
        candidate={candidate}
        selected={selected}
        onSelect={onSelect}
        onNavigate={onNavigate}
      />
    </div>
  );
}